import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Card, formatCurrency, Table, SendIcon } from '@avila-tek/ui/src';
import { createColumnHelper } from '@tanstack/react-table';
import { Benefit, PageInfo, Pagination, Plan } from '../../models';
import { ActiveDotCell } from '../shared';

interface ActionCellProps {
  benefitId: string;
}

function ActionCell({ benefitId }: ActionCellProps) {
  return (
    <div className="flex gap-5">
      <Link href={`/app/benefits/${benefitId}?tab=assign`}>
        <SendIcon className="w-5 h-5 text-purple-300" />
      </Link>
    </div>
  );
}

const columnHelper = createColumnHelper<Benefit>();

const columns = [
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224
  columnHelper.accessor('title', {
    header: 'Nombre',
  }),

  columnHelper.accessor('value', {
    header: 'Importe',
    cell: (info) => `$${formatCurrency(info?.getValue())}`,
  }),

  columnHelper.accessor('active', {
    header: 'Estatus',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),

  columnHelper.display({
    id: 'actions',
    header: 'Acciones',
    cell: (props) => <ActionCell benefitId={props.row.original._id} />,
  }),
];

interface BenefitTableProps {
  data: Pagination<Benefit>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function BenefitTable({ data, pagination, setPagination }: BenefitTableProps) {
  const router = useRouter();

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/benefits/${id}`);
  };

  const onClickAddButton = () => router.push('/app/benefits/create');

  const handleGoToPage = (value: number) => {
    setPagination({ ...pagination, currentPage: value });
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<Benefit>
        data={data.items}
        columns={columns}
        href="/app/benefits/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput />
          <Table.AddButton title="Beneficio" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<Benefit> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: { ...data.pageInfo },
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default BenefitTable;
